// src/encounter/services/use-provider-favourites.ts
// The practitioner's OWN saved favourites per A/P category — the list Flutter's left rail shows BEFORE
// anyone types (PROD-1801 / A/P-17).
//
// WHY THIS EXISTS. The panel was headed "FAVOURITE LABS" while rendering the GLOBAL catalog, and it
// rendered nothing at all until two characters were typed. Flutter does the opposite: it pre-fetches the
// provider's favourites for every category on mount (assessment_plan_search_list_widget.dart:255-264 →
// FetchProvider{Icd,Lab,Medication,Radiology,Procedure,Vaccination,Snomed}UseCase, new_ap_bloc.dart:38-77)
// and filters that in-memory list CLIENT-side with no minimum length (APSLW:673-678). The global catalog
// is a SEPARATE section the user opts into via "Show global results." (APSLW:464-599).
//
// So: favourites first, no query needed; global only on request. This hook is the first half.
//
// ENDPOINT SHAPES, verified against both the Dart and the .NET controller rather than guessed:
//   GET /api/Provider/GetICDCodes    · GetSnomed · GetLabOrder · GetMedication
//                                    · GetVaccination · GetRadiololgy · GetProcedure
//   query params: ProviderId + searchQuery (the BE binds providerId case-insensitively)
//   row: { id, <xxx>Code: { code, display, system }, provider, notes }
//
// `GetRadiololgy` IS MISSPELLED IN THE BACKEND ROUTE ITSELF (ProviderController.cs:551). It is the real
// path — do not "correct" it.
//
// The code key differs per category (icdCode · snomedCode · labOrderCode · medicationCode ·
// vaccinationCode · radiologyCode · procedureCode), which is why each entry below names its own.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';

/** The A/P categories that HAVE provider favourites. Referral and education have none — no entity, no
 *  DbSet, no use case, and Flutter's favourites dialog renders only these six plus SNOMED. */
export type FavouriteCategory =
  | 'diagnosis'
  | 'lab'
  | 'medication'
  | 'vaccination'
  | 'imaging'
  | 'procedure';

interface Endpoint {
  path: string;
  /** The per-category code key on each row. */
  codeKey: string;
}

const ENDPOINTS: Record<FavouriteCategory, Endpoint> = {
  diagnosis: { path: 'GetICDCodes', codeKey: 'icdCode' },
  lab: { path: 'GetLabOrder', codeKey: 'labOrderCode' },
  medication: { path: 'GetMedication', codeKey: 'medicationCode' },
  vaccination: { path: 'GetVaccination', codeKey: 'vaccinationCode' },
  // Misspelled in the BE route. Real path.
  imaging: { path: 'GetRadiololgy', codeKey: 'radiologyCode' },
  procedure: { path: 'GetProcedure', codeKey: 'procedureCode' },
};

interface Coding {
  code?: string | null;
  display?: string | null;
  displayName?: string | null;
  system?: string | null;
}

/** One favourite row. The code lives under a per-category key, so read it dynamically. */
type FavouriteRow = Record<string, unknown> & { id?: string | null; notes?: string | null };

function toResult(row: FavouriteRow, codeKey: string): CodeSearchResult | null {
  const coding = (row[codeKey] ?? null) as Coding | null;
  if (!coding) return null;
  const code = coding.code?.trim() ?? '';
  // The BE's flat entity stores Display; some reads surface displayName. Accept either.
  const display = (coding.display ?? coding.displayName ?? '').trim();
  if (!code && !display) return null;
  return {
    id: (row.id as string | undefined)?.trim() || code || display,
    code: code || display,
    display: display || code,
    ...(coding.system?.trim() ? { system: coding.system.trim() } : {}),
  };
}

/**
 * The provider's favourites for ONE category. Fetched WITHOUT a query — the whole point is that the
 * list exists before the clinician types; filtering happens client-side (see `filterFavourites`).
 *
 * `enabled` is false without a providerId: an unscoped call would return the master catalog, and
 * labelling that "Favourite …" is exactly the defect this hook fixes.
 */
export function useProviderFavourites(
  providerId: string | null | undefined,
  category: FavouriteCategory,
  enabled = true,
) {
  const ep = ENDPOINTS[category];
  const pid = providerId?.trim() ?? '';
  return useQuery<CodeSearchResult[]>({
    queryKey: ['provider-favourites', category, pid],
    enabled: enabled && pid.length > 0 && !!ep,
    // Favourites change rarely and are re-read on every category switch — keep them warm rather than
    // re-fetching seven collections each time the clinician flips a tab.
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      // searchQuery is sent EMPTY on purpose: we want the whole favourites list, then filter locally.
      const url = `/api/Provider/${ep.path}?ProviderId=${encodeURIComponent(pid)}&searchQuery=`;
      const rows = await get<FavouriteRow[]>(url, { signal });
      return (rows ?? []).flatMap((r) => {
        const hit = toResult(r, ep.codeKey);
        return hit ? [hit] : [];
      });
    },
  });
}

/**
 * Filter favourites the way Flutter does: in memory, on code OR display, case-insensitive, and with NO
 * minimum length (APSLW:673-678 — `element.containQuery(searchText)`). An empty query returns everything,
 * which is what makes the rail useful the moment the step opens.
 */
export function filterFavourites(items: CodeSearchResult[], query: string): CodeSearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter(
    (i) =>
      (i.display ?? '').toLowerCase().includes(q) || (i.code ?? '').toLowerCase().includes(q),
  );
}

/** Whether a category has provider favourites at all (referral/education do not). */
export function hasFavourites(category: string): category is FavouriteCategory {
  return category in ENDPOINTS;
}
